import {useCallback, useState} from 'react';
import {Content, DialogPortal, Root} from '@radix-ui/react-dialog';
import {NextPage} from 'next';
import {useRouter} from 'next/router';

import {useUserAccountSearchLazyQuery} from '#apollo/schema';
import {Screen, Text} from '#component/base';
import {CreateAccountForm} from '#component/form';
import {CreateAccountValues} from '#component/form/CreateAccountForm';
import {FullscreenLoading, Hero, HorizontalProgressTracker} from '#component/partial';
import {SURVEY_ONBOARDING_GIFTED_KEY} from '#constant/survey';
import {useAuth, useOnceEffect} from '#hook';
import {useReturnUrl} from '#hook';
import {handleRedirectToUrl} from '#hook/useReturnUrl';
import * as DESTINATION from '#hook/useReturnUrl/defaultDestination';
import {sprinkles} from '#style/sprinkles';

import {parseQueryParams} from './CreateAccount.util';

import * as css from './CreateAccount.css';

export const CreateAccount: NextPage = () => {
  const router = useRouter();
  const [signUpLoading, setSignupLoading] = useState<boolean>(false);
  const [renderContent, setRenderContent] = useState<boolean>(false);
  const {signUp, isUserSignedIn} = useAuth();
  const {targetResolver} = useReturnUrl();
  const {email, firstName, lastName, autoVerify, returnUrl, isGiftedKitRecipient} =
    parseQueryParams(router.query);
  const [userSearchAccount] = useUserAccountSearchLazyQuery({variables: {email: email || ''}});

  const onPageLoad = useCallback(async () => {
    if (isGiftedKitRecipient) {
      setRenderContent(true);
      return;
    }
    const isSignedIn = await isUserSignedIn({async: true});
    const {data} = await userSearchAccount();
    const hasAccount = data?.searchAccount?.hasAccount || false;
    const resolveReturnUrl = targetResolver(returnUrl);

    if (isSignedIn || hasAccount) {
      await handleRedirectToUrl(router, resolveReturnUrl, DESTINATION.HOME);
    } else {
      setRenderContent(true);
    }
  }, [isUserSignedIn, userSearchAccount, targetResolver, returnUrl, router, isGiftedKitRecipient]);

  useOnceEffect(() => {
    onPageLoad();
  });

  const handleSubmit = async ({acceptTerms, ...formValues}: CreateAccountValues) => {
    try {
      // Put up the fullscreen loading view while the sign-up call is being
      // made, and never take it down ...
      setSignupLoading(true);
      await signUp(
        {
          email: formValues.email,
          phoneNumber: formValues.phoneNumber,
          firstName: formValues.firstName,
          lastName: formValues.lastName,
          password: formValues.password,
          birthday: formValues.birthday,
          birthSex: formValues.birthSex,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          autoVerify,
          isGiftedTestKit: isGiftedKitRecipient,
        },
        {
          redirect: targetResolver(
            returnUrl ||
              `${DESTINATION.ONBOARDING}${
                isGiftedKitRecipient ? `?survey=${SURVEY_ONBOARDING_GIFTED_KEY}` : ''
              }`,
          ),
        },
      );
    } catch (err) {
      // ... unless the sign-up call itself fails for whatever reasons.
      setSignupLoading(false);
      // ... and re-throw the error here so consumers of this `handleSubmit`
      // function can still catch them.
      throw err;
    }
  };

  const handleSubmitError = () => {
    window.scrollTo({top: 150});
  };

  const hasPrefilledInformation = Boolean(email);

  const getProgressTrackerItems = () => {
    const baseItems = [
      {
        title: 'Create Account',
      },
      {
        title: 'Account Setup',
      },
    ];
    if (isGiftedKitRecipient) {
      return baseItems;
    }
    return [
      {
        title: 'Order Confirmed',
      },
      ...baseItems,
    ];
  };
  const progressTrackerItems = getProgressTrackerItems();
  return (
    <Root open={signUpLoading}>
      <Screen
        withDefaultSizing={false}
        contentContainerClassName={sprinkles({
          flex: 'flex',
          dir: 'col',
        })}
        // Because this page also handles redirection to return URL, we should
        // not set the `userState` prop to disable user state checks. This way
        // we can avoid potential conflicts and complications.
        renderContent={renderContent}
        pageName="Create Account">
        <Hero showOverlap={false} variant="sunrise">
          <div className={css.hero}>
            <Text
              className={sprinkles({w: 'full'})}
              color="deepBurgundy"
              variant="heading1"
              textAlign="center">
              Create Your Account
            </Text>
            {isGiftedKitRecipient && (
              <Text className={sprinkles({w: 'full', pX: 24})} color="doveGray" textAlign="center">
                To register your test, you must first create an account.
              </Text>
            )}
            {hasPrefilledInformation && (
              <Text className={sprinkles({w: 'full', pX: 24})} color="doveGray" textAlign="center">
                We&apos;ve prefilled information from your order to make registration easier.
              </Text>
            )}
            <div className={sprinkles({w: 'full', pX: 24})}>
              <HorizontalProgressTracker
                items={progressTrackerItems}
                currentItemIndex={progressTrackerItems.findIndex(
                  step => step.title === 'Create Account',
                )}
                currentItemStatus="in-progress"
              />
            </div>
          </div>
        </Hero>

        <div className={css.contentWrapper}>
          <div className={css.content}>
            <div className={css.formWrapper}>
              <CreateAccountForm
                returnUrl={returnUrl}
                email={email}
                firstName={firstName}
                lastName={lastName}
                emailEnabled={isGiftedKitRecipient}
                hideLoginRedirectFooter={isGiftedKitRecipient}
                onSubmit={handleSubmit}
                onError={handleSubmitError}
              />
            </div>
          </div>
        </div>
      </Screen>
      {
        // We can't use the `isLoading` (and the `<FullscreenLoading />`) in
        // the `<Screen />` component because that one is mutually exclusive
        // with the content. That means if we need to re-show the content on
        // this page (for example if the `signup` call fails) then the signup
        // form on the page would be empty. So in order to preserve existing
        // content on this page, we use a separate `<FullscreenLoading />`
        // component that simply covers up the content.
      }
      <DialogPortal>
        <Content>
          <FullscreenLoading
            text={signUpLoading ? 'Account Creation Underway' : ''}
            subText={
              signUpLoading
                ? 'Give us a moment, as we validate your details. Please refrain from refreshing or closing this screen.'
                : ''
            }
          />
        </Content>
      </DialogPortal>
    </Root>
  );
};
